import { Button, Checkbox, ListItemText, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../../DataLayer/DataLayer";
import Layout from "../../../../../Layout";
import CustomHeaderTitle from "../../../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../../../components/UI/CustomizedTable";
import ReportDownloads from "../../../../../components/ReportDownloads";
import SwitchBar from "../../../../../components/Pages/SwitchBar";
import {
  getEntertainmentSummaryReport,
  getEntertainmentRvcs,
} from "../../../../../utils/ApiCalls/BackOffice/Entertainment";
import { createReportDownload } from "../../../../../utils/ApiCalls/ReportDownloads";
import { addlog } from "../../../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../../../utils/Constant/LogConstant";
import { getReportFileName } from "../../../../../utils/Constant/ReportDownloadLabels";
import { checkPermission } from "../../../../../utils/Validation/checkPermission";

const { RangePicker } = DatePicker;
const PAGE_PATH = "/shore/back-office/entertainment/report/summary";

const getDefaultDateRange = () =>
  JSON.stringify([moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]);

const parseDateRange = (range) => {
  if (!range) return null;
  try {
    const [start, end] = JSON.parse(range);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable - treat as no selection
  }
  return null;
};

const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : "-");

const EntertainmentSummaryReport = () => {
  const locationP = useLocation();
  const navigate = useNavigate();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();
  const [topTab, setTopTab] = useState("report");

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/entertainment/report" },
    { title: "Summary Report", route: PAGE_PATH },
  ];

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("dateRange") ? params.get("dateRange") : getDefaultDateRange();
  });
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const parseIdList = (val) => (val ? val.split(",").filter(Boolean) : []);
  const [rvcIds, setRvcIds] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("rvcIds"))
  );
  const [rvcOptions, setRvcOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const rvcRes = await getEntertainmentRvcs({ limit: 1000 });
      const sortAlphaNumeric = (a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
      setRvcOptions(
        (rvcRes?.data || [])
          .slice()
          .sort((a, b) => sortAlphaNumeric(`${a.rvcId}-${a.name}`, `${b.rvcId}-${b.name}`))
      );
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (rvcIds.length) params.set("rvcIds", rvcIds.join(","));
    params.set("dateRange", dateRange);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, dateRange, rvcIds]);

  const columns = [
    {
      title: "Sl",
      key: "sl",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Fidelio Business Date",
      dataIndex: "fidelioBusinessDate",
      key: "fidelioBusinessDate",
      width: 150,
      render: (val) => (val ? moment(val).format("YYYY-MM-DD") : "-"),
    },
    { title: "RVC Number", dataIndex: "rvcNumber", key: "rvcNumber", width: 110 },
    { title: "RVC Name", dataIndex: "rvcName", key: "rvcName", width: 150, render: (val) => val?.english },
    { title: "Item Number", dataIndex: "itemNumber", key: "itemNumber", width: 110 },
    { title: "Item Name", dataIndex: "itemName", key: "itemName", width: 160, render: (val) => val?.english },
    { title: "Transaction Count", dataIndex: "transactionCount", key: "transactionCount", width: 130 },
    { title: "Sold Quantity", dataIndex: "soldQuantity", key: "soldQuantity", width: 120 },
    { title: "Voided Quantity", dataIndex: "voidedQuantity", key: "voidedQuantity", width: 130 },
    { title: "Net Quantity", dataIndex: "netQuantity", key: "netQuantity", width: 120 },
    {
      title: "Gross Sale",
      dataIndex: "grossSale",
      key: "grossSale",
      width: 110,
      render: (val) => formatAmount(val),
    },
    { title: "Voided Transaction", dataIndex: "voidedTransaction", key: "voidedTransaction", width: 140 },
    {
      title: "Voided Amount",
      dataIndex: "voidedAmount",
      key: "voidedAmount",
      width: 130,
      render: (val) => formatAmount(val),
    },
    {
      title: "Net Sales",
      dataIndex: "netSales",
      key: "netSales",
      width: 110,
      render: (val) => formatAmount(val),
    },
  ];

  const buildFilterArgs = () => {
    const args = {};
    args.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (rvcIds.length) args.rvcIds = rvcIds.join(",");
    try {
      const [fromDate, toDate] = JSON.parse(dateRange || "[]");
      if (fromDate && toDate) {
        args.fromDate = fromDate;
        args.toDate = toDate;
      }
    } catch (e) {
      // no date range selected
    }
    return args;
  };

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = { page: value || page, limit: pageSize, ...buildFilterArgs() };
    let data = await getEntertainmentSummaryReport(args);
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setRows(data.data || []);
      setCount(data.totalCount || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const handleRequestDownload = async () => {
    if (!checkPermission(permission, "download_per", "Summary Report", window?.location?.pathname)) {
      message.error("Download permission missing");
      return;
    }
    const res = await createReportDownload({
      moduleName: "entertainment_summary_report",
      createdBy: userDetails.id,
      searchParams: buildFilterArgs(),
    });
    if (res?.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
    } else {
      addlog(
        LOG_ACTIONS.REQUESTED,
        LOG_TYPES.REPORT_DOWNLOADS,
        {
          title: "Summary Report download requested",
          moduleName: "entertainment_summary_report",
          reportDownloadId: res?.id,
          fileName: getReportFileName("entertainment_summary_report", res?.createdAt, res?.id, res?.timezone),
        },
        userDetails.id
      );
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: "Download requested - check Download Logs tab", type: "success" },
      });
    }
  };

  useEffect(() => {
    getData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, dateRange, rvcIds]);

  return (
    <Layout type={"read_per"} module_data={"Summary Report"}>
      <CustomHeaderTitle title={"Summary Report"} />
      <div>
        <HeaderBread navigation={navigation} title={"Summary Report"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Summary Report", value: "report" },
              { title: "Download Logs", value: "downloads" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab === "downloads" ? (
            <ReportDownloads module="entertainment_summary_report" />
          ) : (
          <div className="card">
            <div className="card-header two_item" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Select
                  multiple
                  style={{ width: "220px", height: "40px" }}
                  displayEmpty
                  value={rvcIds}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRvcIds(newValue.includes("__ALL__") ? [] : newValue);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? rvcOptions
                          .filter((rvc) => selected.includes(String(rvc.id)))
                          .map((rvc) => `${rvc.rvcId}-${rvc.name}`)
                          .join(", ")
                      : "Filter by Revenue Center"
                  }
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={rvcIds.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {rvcOptions.map((rvc) => (
                    <MenuItem key={rvc.id} value={String(rvc.id)}>
                      <Checkbox checked={rvcIds.includes(String(rvc.id))} />
                      <ListItemText primary={`${rvc.rvcId}-${rvc.name}`} />
                    </MenuItem>
                  ))}
                </Select>
                <RangePicker
                  style={{ width: "250px" }}
                  onChange={(date, dateString) => {
                    setDateRange(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseDateRange(dateRange)}
                  format={"YYYY-MM-DD"}
                />
                <Button
                  variant="contained"
                  style={{ background: "var(--red)", fontWeight: "500" }}
                  onClick={() => {
                    setDateRange(getDefaultDateRange());
                    setRvcIds([]);
                    setPage(1);
                  }}
                >
                  Reset Filter
                </Button>
                <Button
                  variant="contained"
                  style={{ background: "#1565c0", fontWeight: "500" }}
                  onClick={handleRequestDownload}
                >
                  Request Download
                </Button>
              </div>
            </div>
            <div className="card-body">
              <div className="table_holder">
                <CustomizedTable
                  setRows={setRows}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSize={pageSize}
                  page={page}
                  count={count}
                  columns={columns}
                  rows={rows}
                  title={"Summary Report"}
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EntertainmentSummaryReport;
